import { useEffect, useMemo } from 'react';

import { TOOL_CONST } from '@/constant';
import { useNewLabelInFrame } from '@/hook';
import { useLabelingBoxContext } from '@/provider';
import { useReviewStore, useTaskFrameStore } from '@/store';
import type { FrameData, Project, Task } from '@/type';
import { isEqualBoxList } from '@/util';

const UPSERT_LABEL_DEBOUNCE_TIME = 50;

const FrameDataAutoSaver = ({
  frameSeq,
  projectID,
  taskID,
}: {
  frameSeq: FrameData['frameSeq'];
  projectID: Project['id'];
  taskID: Task['id'];
  refetch?: () => Promise<void>;
}) => {
  const { currentFrame } = useTaskFrameStore();
  const pcdIndexReviewBoxListMap = useReviewStore(
    state => state.pcdIndexReviewBoxListMap,
  );
  const reviewComment = useReviewStore(state => state.comment);
  const { labelingBoxList } = useLabelingBoxContext();
  const { mutateAsync: upsertLabel, isPending: isUpsertLabelPending } =
    useNewLabelInFrame({
      frameSeq,
      projectID,
      taskID,
    });
  const currentLabelingBoxList = labelingBoxList
    ?.filter(
      box => box.workType !== TOOL_CONST.TOOL_LABELING_BOX_WORK_TYPE.REVIEW,
    )
    ?.map(box => box.toJSONForMiri())
    .toSorted((a, b) => b.id.localeCompare(a.id));
  const currentReviewBoxList = useMemo(
    () => useReviewStore.getState().getCurrentPcdIndexReviewBoxList(),
    [pcdIndexReviewBoxListMap],
  );
  const fechedWorkData = currentFrame?.workData ?? [];
  const fechedReviewData = currentFrame?.reviewData ?? [];
  const workState = currentFrame?.workState;
  const disabledSync =
    (isEqualBoxList(fechedWorkData, currentLabelingBoxList) &&
      isEqualBoxList(fechedReviewData, currentReviewBoxList)) ||
    isUpsertLabelPending;

  useEffect(() => {
    if (disabledSync) return;

    const timeoutId = setTimeout(() => {
      upsertLabel({
        workData: currentLabelingBoxList,
        workState,
        reviewData: currentReviewBoxList,
        reviewComment,
      });
    }, UPSERT_LABEL_DEBOUNCE_TIME);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [
    disabledSync,
    currentLabelingBoxList,
    currentReviewBoxList,
    reviewComment,
  ]);

  return <></>;
};

FrameDataAutoSaver.displayName = 'FrameDataAutoSaver';

export default FrameDataAutoSaver;
